import { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Images, Pencil, Store } from 'lucide-react';
import { DECOR_LIBRARY_CATEGORY_SPECS, type DecorLibraryItem } from '@peles/shared';
import { lookColors, lookSummary } from './decorFields';

export interface DecorLibraryCardProps {
  item: DecorLibraryItem;
  selected?: boolean;
  /** Pressing the card: pick it, or open it to edit. */
  onPress: (item: DecorLibraryItem) => void;
  /** The pencil — offered beside a pick. */
  onEdit?: (item: DecorLibraryItem) => void;
  /** The label pressing the card reads as, for screen readers: "Choose", "Edit". */
  pressLabel: string;
}

/**
 * A piece of the decor library as a card: its pictures (step through the
 * angles without opening it), name, kind, what the 3D view draws, its colours
 * and who supplies it.
 */
export function DecorLibraryCard({
  item,
  selected,
  onPress,
  onEdit,
  pressLabel,
}: DecorLibraryCardProps) {
  const [angle, setAngle] = useState(0);
  const pictures = item.pictures.filter((p) => p.url);
  const shown = pictures[Math.min(angle, Math.max(pictures.length - 1, 0))];
  const summary = lookSummary(item.category, item.look);
  const colors = [...new Set([...lookColors(item.look), ...item.colors])].slice(0, 6);

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        selected
          ? 'border-primary-500 ring-2 ring-primary-200'
          : 'border-gray-200 hover:border-gray-300'
      } ${item.isActive ? '' : 'opacity-60'}`}
    >
      <div className="relative aspect-[4/3] bg-gray-100">
        {shown ? (
          <img
            src={shown.url!}
            alt={item.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            <Images className="h-8 w-8" aria-hidden />
          </div>
        )}
        {pictures.length > 1 && (
          <>
            <button
              type="button"
              aria-label={`Previous picture of ${item.name}`}
              onClick={() => setAngle((a) => (a - 1 + pictures.length) % pictures.length)}
              className="absolute left-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-gray-700 shadow sm:opacity-0 sm:group-hover:opacity-100"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`Next picture of ${item.name}`}
              onClick={() => setAngle((a) => (a + 1) % pictures.length)}
              className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-gray-700 shadow sm:opacity-0 sm:group-hover:opacity-100"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="absolute bottom-1.5 right-1.5 rounded-full bg-gray-900/70 px-2 py-0.5 text-[11px] text-white">
              {Math.min(angle, pictures.length - 1) + 1} / {pictures.length}
            </span>
          </>
        )}
        {selected && (
          <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-primary-600 px-2 py-0.5 text-[11px] font-medium text-white">
            <Check className="h-3 w-3" /> Chosen
          </span>
        )}
        {!item.isActive && (
          <span className="absolute left-1.5 top-1.5 rounded-full bg-gray-800/80 px-2 py-0.5 text-[11px] text-white">
            Retired
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => onPress(item)}
        aria-label={`${pressLabel} ${item.name}`}
        className="flex flex-1 flex-col gap-1 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {DECOR_LIBRARY_CATEGORY_SPECS[item.category].label}
        </span>
        <span className="line-clamp-2 text-sm font-semibold text-gray-900">{item.name}</span>
        {summary.length > 0 && (
          <span className="line-clamp-1 text-xs text-gray-600">{summary.join(' · ')}</span>
        )}
        {colors.length > 0 && (
          <span className="flex gap-1 pt-0.5" aria-hidden>
            {colors.map((c) => (
              <span
                key={c}
                className="h-4 w-4 rounded-full border border-gray-200"
                style={{ backgroundColor: c }}
              />
            ))}
          </span>
        )}
        {item.vendors.length > 0 && (
          <span className="flex items-center gap-1 pt-0.5 text-xs text-gray-500">
            <Store className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{item.vendors.map((v) => v.name).join(', ')}</span>
          </span>
        )}
      </button>
      {onEdit && (
        <button
          type="button"
          aria-label={`Edit ${item.name}`}
          onClick={() => onEdit(item)}
          className="absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow hover:text-gray-900"
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </article>
  );
}
